import React, { useState } from 'react';
import { LockIcon, PlayIcon } from 'lucide-react';
import { Reveal } from './Reveal';
import { GlowFrame } from './GlowFrame';
import { VimeoModal } from './VimeoModal';
import { FormModal } from './FormModal';
import type { FormFieldConfig } from '../types/contact';
interface ScreeningFilm {
  id: string;
  title: string;
  runtime: string;
  still: string;
  vimeoId: string;
  vimeoHash: string;
}
const SCREENING_FILMS: ScreeningFilm[] = [{
  id: 'highlights',
  title: 'Cinematic Highlights',
  runtime: '45 min',
  still: 'https://cdn.magicpatterns.com/uploads/qgfHRhgLfi15nwEQRgGCcX/flashes.jpg',
  vimeoId: '1227371613',
  vimeoHash: '175adefc73'
}, {
  id: 'full-show',
  title: 'Full Show — Technical Recording',
  runtime: 'Full length',
  still: 'https://cdn.magicpatterns.com/uploads/cEqZ21JZsMjUyGwWN6XiZk/mary.jpg',
  vimeoId: '1225592971',
  vimeoHash: '3c705496f8'
}];
const ACCESS_FIELDS: FormFieldConfig[] = [{
  id: 'name',
  label: 'Name',
  type: 'text',
  required: true,
  maxLength: 100
}, {
  id: 'organisation',
  label: 'Organisation',
  type: 'text',
  required: true,
  maxLength: 150
}, {
  id: 'contact',
  label: 'Contact information',
  type: 'text',
  required: true,
  placeholder: 'Email or phone',
  maxLength: 200
}, {
  id: 'reason',
  label: 'Reason for requesting access',
  type: 'textarea',
  required: true,
  maxLength: 2000
}];
export function ScreeningRoom() {
  const [activeFilm, setActiveFilm] = useState<ScreeningFilm | null>(null);
  const [requestOpen, setRequestOpen] = useState(false);
  return <section id="screening-room" aria-label="Screening room" className="bg-ink px-5 py-24 sm:px-8 lg:px-12 lg:py-32" data-id="element-162" data-mp="r03gtcadjs86">
      <div className="mx-auto max-w-5xl" data-id="element-163" data-mp="3undlozpkwin2">
        <Reveal data-id="element-164" data-mp="3umc8aol3bk26">
          <p className="mb-3 text-center text-[11px] uppercase tracking-[0.3em] text-ice" data-id="element-165" data-mp="3ulsrxcb393q9">
            For presenters
          </p>
          <h2 className="mb-4 text-center font-heading text-4xl uppercase tracking-tight text-led sm:text-5xl" data-id="element-166" data-mp="j7a6gy22w99i">
            Screening Room
          </h2>
          <p className="mx-auto mb-12 max-w-xl text-center text-sm text-led/60" data-id="element-167" data-mp="n7cahdvadery">
            Enter the password inside the player to watch the technical recording or the cinematic
            highlights reel.
          </p>
        </Reveal>

        <div className="grid gap-6 sm:grid-cols-2" data-id="element-168" data-mp="8fshrafvcue">
          {SCREENING_FILMS.map((film, index) => <Reveal key={film.id} delay={index * 0.1} data-id="element-169" data-mp="4a199nq3gb9v" data-mpi={index}>
              <GlowFrame as="button" interactive onClick={() => setActiveFilm(film)} className="group relative block aspect-[4/5] w-full overflow-hidden text-left" data-id="element-170" data-mp="4ajfnd6h05vi">
                <img src={film.still} alt="" aria-hidden="true" loading="lazy" className="absolute inset-0 h-full w-full object-cover opacity-60 transition-opacity duration-300 group-hover:opacity-75" data-id="element-171" data-mp="1kojeektkxph6" />
                <div className="absolute inset-0 bg-gradient-to-t from-ink via-ink/40 to-transparent" data-id="element-172" data-mp="355s99791swj7" />
                <div className="absolute inset-0 flex items-center justify-center" data-id="element-173" data-mp="30f3ftmdt5phf">
                  <span className="flex h-14 w-14 items-center justify-center rounded-full border border-led/40 text-led" data-id="element-174" data-mp="2vgs7jg35vn1b">
                    <PlayIcon className="h-5 w-5 translate-x-0.5" aria-hidden="true" data-id="element-175" data-mp="2rf6y64pu5qvv" />
                  </span>
                </div>
                <div className="absolute inset-x-0 bottom-0 flex items-center justify-between px-5 py-4" data-id="element-176" data-mp="2meiykunjk1q1">
                  <div data-id="element-177" data-mp="2hgzidp7a9evg">
                    <p className="font-heading text-lg uppercase tracking-tight text-led" data-id="element-178" data-mp="2cho2sjinkffg">
                      {film.title}
                    </p>
                    <p className="mt-1 text-[10px] uppercase tracking-[0.2em] text-led/55" data-id="element-179" data-mp="fo331q6cbq5j">
                      {film.runtime}
                    </p>
                  </div>
                  <LockIcon className="h-4 w-4 text-led/55" aria-hidden="true" data-id="element-180" data-mp="iqpmr6xa2upn" />
                </div>
              </GlowFrame>
            </Reveal>)}
        </div>

        <Reveal delay={0.2} data-id="element-181" data-mp="8stdkknwi9u6">
          <div className="mt-10 text-center" data-id="element-182" data-mp="8tkxgy84yj4m">
            <button type="button" onClick={() => setRequestOpen(true)} className="text-xs uppercase tracking-[0.2em] text-led/65 underline decoration-led/30 underline-offset-4 transition-colors duration-300 hover:text-ice focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ice" data-id="element-183" data-mp="8uw0uwxo5ov7">
              Need access? Request it →
            </button>
          </div>
        </Reveal>
      </div>

      <VimeoModal isOpen={activeFilm !== null} onClose={() => setActiveFilm(null)} title={activeFilm?.title || 'Screening'} vimeoId={activeFilm?.vimeoId || ''} vimeoHash={activeFilm?.vimeoHash || ''} data-id="element-184" data-mp="3r7u7tnsvphyv" />

      <FormModal isOpen={requestOpen} onClose={() => setRequestOpen(false)} title="Request Screening Access" description="Tell us who you are and we will send the password." requestType="Screening Access" fields={ACCESS_FIELDS} submitLabel="Send request" data-id="element-185" data-mp="3hafrdo6ygt6k" />
    </section>;
}